const CAMPAIGN_BOOTSTRAP_STORAGE_KEY = 'motito_campaign_bootstrap_v1';
const CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY =
  'motito_campaign_bootstrap_pending_token_v1';
const CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_TTL_MS = 15 * 60 * 1000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export interface ExistingUserCampaignBootstrapResponse {
  status: 'existing_user';
  access_token: string;
  refresh_token: string;
  expires_in?: number;
  session_id?: string;
}

export interface NewUserCampaignBootstrapResponse {
  status: 'new_user';
  bootstrap_session_id: string;
  phone: string;
  expires_in: number;
}

export type CampaignBootstrapResponse =
  | ExistingUserCampaignBootstrapResponse
  | NewUserCampaignBootstrapResponse;

export interface CampaignBootstrapSession {
  bootstrapSessionId: string;
  phoneNumber: string;
  expiresAt: string;
}

interface StoredPendingCampaignBootstrapToken {
  token: string;
  createdAt: number;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function getStorage(storage?: StorageLike): StorageLike | null {
  if (storage) {
    return storage;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function clearStoredValue(storage: StorageLike | null, key: string): void {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Storage persistence is best-effort.
  }
}

export function sanitizeCampaignBootstrapToken(value: unknown): string | null {
  const normalized = normalizeString(value);
  if (!normalized || normalized.length > 255) {
    return null;
  }

  return normalized;
}

export function extractCampaignBootstrapToken(url: URL): string | null {
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  const hashToken = hash
    ? sanitizeCampaignBootstrapToken(new URLSearchParams(hash).get('token'))
    : null;

  if (hashToken) {
    return hashToken;
  }

  return sanitizeCampaignBootstrapToken(url.searchParams.get('token'));
}

export function scrubCampaignBootstrapToken(url: URL): string {
  const scrubbed = new URL(url.toString());
  scrubbed.hash = '';
  scrubbed.searchParams.delete('token');
  return `${scrubbed.pathname}${scrubbed.search}${scrubbed.hash}`;
}

export function buildCampaignBootstrapSession(
  response: NewUserCampaignBootstrapResponse
): CampaignBootstrapSession {
  return {
    bootstrapSessionId: response.bootstrap_session_id,
    phoneNumber: response.phone,
    expiresAt: new Date(Date.now() + response.expires_in * 1000).toISOString(),
  };
}

export function readCampaignBootstrapSession(
  storage?: StorageLike
): CampaignBootstrapSession | null {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return null;
  }

  try {
    const raw = resolvedStorage.getItem(CAMPAIGN_BOOTSTRAP_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<CampaignBootstrapSession>;
    const bootstrapSessionId = normalizeString(parsed.bootstrapSessionId);
    const phoneNumber = normalizeString(parsed.phoneNumber);
    const expiresAt = normalizeString(parsed.expiresAt);

    if (!bootstrapSessionId || !phoneNumber || !expiresAt) {
      return null;
    }

    const expiryTime = new Date(expiresAt).getTime();
    if (!Number.isFinite(expiryTime) || expiryTime <= Date.now()) {
      return null;
    }

    return {
      bootstrapSessionId,
      phoneNumber,
      expiresAt,
    };
  } catch {
    return null;
  }
}

export function persistCampaignBootstrapSession(
  session: CampaignBootstrapSession,
  storage?: StorageLike
): void {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return;
  }

  try {
    resolvedStorage.setItem(
      CAMPAIGN_BOOTSTRAP_STORAGE_KEY,
      JSON.stringify(session)
    );
  } catch {
    // Storage persistence is best-effort.
  }
}

export function clearCampaignBootstrapSession(storage?: StorageLike): void {
  clearStoredValue(getStorage(storage), CAMPAIGN_BOOTSTRAP_STORAGE_KEY);
}

export function persistPendingCampaignBootstrapToken(
  token: string,
  storage?: StorageLike
): void {
  const resolvedStorage = getStorage(storage);
  const sanitizedToken = sanitizeCampaignBootstrapToken(token);
  if (!resolvedStorage || !sanitizedToken) {
    return;
  }

  try {
    const record: StoredPendingCampaignBootstrapToken = {
      token: sanitizedToken,
      createdAt: Date.now(),
    };
    resolvedStorage.setItem(
      CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY,
      JSON.stringify(record)
    );
  } catch {
    // Storage persistence is best-effort.
  }
}

export function readPendingCampaignBootstrapToken(
  storage?: StorageLike
): string | null {
  const resolvedStorage = getStorage(storage);
  if (!resolvedStorage) {
    return null;
  }

  try {
    const raw = resolvedStorage.getItem(
      CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY
    );
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredPendingCampaignBootstrapToken>;
    const token = sanitizeCampaignBootstrapToken(parsed.token);
    const createdAt = Number(parsed.createdAt ?? 0);

    if (!token || createdAt <= 0) {
      clearStoredValue(
        resolvedStorage,
        CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY
      );
      return null;
    }

    if (Date.now() - createdAt > CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_TTL_MS) {
      clearStoredValue(
        resolvedStorage,
        CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY
      );
      return null;
    }

    return token;
  } catch {
    clearStoredValue(
      resolvedStorage,
      CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY
    );
    return null;
  }
}

export function clearPendingCampaignBootstrapToken(
  storage?: StorageLike
): void {
  clearStoredValue(
    getStorage(storage),
    CAMPAIGN_BOOTSTRAP_PENDING_TOKEN_STORAGE_KEY
  );
}
